import React, { useState } from "react";
import {
  Table,
  Button,
  Space,
  Card,
  Typography,
  Modal,
  Form,
  Row,
  Col,
  message,
  Spin,
  InputNumber,
  Select,
  Alert,
} from "antd";
import { DollarOutlined, EditOutlined, SwapOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import moment from "moment";
import { exchangeRateService, type ConvertCurrencyRequest, type UpdateExchangeRateRequest } from "../services/exchangeRateService";

const { Title, Text } = Typography;
const { Option } = Select;

const FlexibleExchangeRateUpdate: React.FC<{
  onUpdate: (rates: any) => void;
  isLoading: boolean;
  currentRates: any;
  onCancel: () => void;
}> = ({ onUpdate, isLoading, currentRates, onCancel }) => {
  const [rates, setRates] = useState({
    usdToLak: currentRates['USD_LAK']?.rate || 21850,
    thbToLak: currentRates['THB_LAK']?.rate || 610.47,
  });

  const handleSubmit = () => {
    const payload = {
      "USD_LAK": rates.usdToLak,
      "THB_LAK": rates.thbToLak,
    };
    onUpdate(payload);
  };

  return (
    <div>
      <Alert
        message="ອັບເດດອັດຕາແລກປ່ຽນ"
        description="ກຳນົດອັດຕາແລກປ່ຽນສຳລັບ USD → LAK ແລະ THB → LAK"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Row gutter={16}>
        <Col span={24}>
          <Card title="ອັດຕາແລກປ່ຽນ" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>1 USD = ? LAK</Text>
                <InputNumber
                  style={{ width: '100%', marginTop: 8 }}
                  value={rates.usdToLak}
                  onChange={(value) => setRates({ ...rates, usdToLak: value || 0 })}
                  placeholder="ເຊັ່ນ 21850"
                  min={0}
                  step={10}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, '') || ''}
                />
              </div>
              <div style={{ marginTop: 16 }}>
                <Text strong>1 THB = ? LAK</Text>
                <InputNumber
                  style={{ width: '100%', marginTop: 8 }}
                  value={rates.thbToLak}
                  onChange={(value) => setRates({ ...rates, thbToLak: value || 0 })}
                  placeholder="ເຊັ່ນ 610.47"
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <Space style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={isLoading}
        >
          ອັບເດດ
        </Button>
        <Button onClick={onCancel} disabled={isLoading}>
          ຍົກເລິກ
        </Button>
      </Space>
    </div>
  );
};

const ExchangeRatesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [convertModalVisible, setConvertModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [convertForm] = Form.useForm();

  const { data: ratesResponse, isLoading } = useQuery(
    ['exchangeRates'],
    () => exchangeRateService.getExchangeRates(),
    {
      onError: (error: unknown) => {
        message.error('ບໍ່ສາມາດດຶງຂໍ້ມູນໄດ້');
        console.error(error);
      },
    }
  );

  const updateRatesMutation = useMutation(
    (data: UpdateExchangeRateRequest) => exchangeRateService.updateExchangeRate(data),
    {
      onSuccess: () => {
        message.success('ອັບເດດອັດຕາແລກປ່ຽນສຳເລັດ');
        queryClient.invalidateQueries(['exchangeRates']);
        setUpdateModalVisible(false);
        form.resetFields();
      },
      onError: (error: unknown) => {
        message.error('ບໍ່ສາມາດອັບເດດອັດຕາແລກປ່ຽນ');
        console.error(error);
      },
    }
  );

  const convertMutation = useMutation(
    (data: ConvertCurrencyRequest) => exchangeRateService.calculateConversion(data),
    {
      onSuccess: (result) => {
        message.success(
          `${convertForm.getFieldValue('amount')} ${convertForm.getFieldValue('fromCurrency')} = ${result.data.convertedAmount.toFixed(2)} ${convertForm.getFieldValue('toCurrency')}`
        );
      },
      onError: (error: unknown) => {
        message.error('ບໍ່ສາມາດຄຳນວນການປ່ຽນສະກຸນເງິນໄດ້');
        console.error(error);
      },
    }
  );

  const handleUpdateRates = (rates: any) => {
    updateRatesMutation.mutate({ rates });
  };

  const handleConvert = async (values: any) => {
    try {
      await convertMutation.mutateAsync(values);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const ratesData = ratesResponse?.data || {};
  const tableData = [
    { key: 'USD_LAK', baseCurrency: 'USD', targetCurrency: 'LAK', label: 'USD → LAK', rate: ratesData['USD_LAK']?.rate || 0, updatedAt: ratesData['USD_LAK']?.updatedAt },
    { key: 'THB_LAK', baseCurrency: 'THB', targetCurrency: 'LAK', label: 'THB → LAK', rate: ratesData['THB_LAK']?.rate || 0, updatedAt: ratesData['THB_LAK']?.updatedAt },
  ];

  const columns = [
    {
      title: "ຄູ່ສະກຸນເງິນ",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "ອັດຄ່າແລກປ່ຽນ",
      dataIndex: "rate",
      key: "rate",
      render: (rate: number) => exchangeRateService.formatRate(rate),
    },
    {
      title: "ອັບເດດລ່າສຸດ",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (updatedAt: string) => updatedAt ? moment(updatedAt).format('DD/MM/YYYY HH:mm') : 'N/A',
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>
          <DollarOutlined style={{ marginRight: 8 }} />
          ອັດຕາແລກປ່ຽນ
        </Title>
        <Space>
          <Button
            icon={<SwapOutlined />}
            onClick={() => setConvertModalVisible(true)}
          >
            ແປງສະກຸນເງິນ
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => setUpdateModalVisible(true)}
            type="primary"
          >
            ອັບເດດ
          </Button>
        </Space>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={tableData}
          rowKey="key"
          loading={isLoading}
          pagination={false}
        />
      </Card>

      <Modal
        title="ອັບເດດອັດຕາແລກປ່ຽນ"
        open={updateModalVisible}
        onCancel={() => {
          setUpdateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <FlexibleExchangeRateUpdate
          onUpdate={handleUpdateRates}
          isLoading={updateRatesMutation.isLoading}
          currentRates={ratesData}
          onCancel={() => {
            setUpdateModalVisible(false);
            form.resetFields();
          }}
        />
      </Modal>

      <Modal
        title="แปลงสกุลเงิน"
        open={convertModalVisible}
        onCancel={() => {
          setConvertModalVisible(false);
          convertForm.resetFields();
        }}
        footer={null}
        width={400}
      >
        <Spin spinning={convertMutation.isLoading}>
          <Form form={convertForm} layout="vertical" onFinish={handleConvert}>
            <Form.Item
              name="amount"
              label="จำนวนเงิน"
              rules={[{ required: true, message: "กรุณาป้อนจำนวนเงิน" }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="จำนวนเงิน"
                min={0}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value:any) => value?.replace(/\$\s?|(,*)/g, '') || ''}
              />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="fromCurrency"
                  label="จากสกุลเงิน"
                  rules={[{ required: true, message: "เลือกสกุลเงินต้นทาง" }]}
                  initialValue="LAK"
                >
                  <Select>
                    <Option value="LAK">₭ LAK</Option>
                    <Option value="THB">฿ THB</Option>
                    <Option value="USD">$ USD</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="toCurrency"
                  label="เป็นสกุลเงิน"
                  rules={[{ required: true, message: "เลือกสกุลเงินปลายทาง" }]}
                  initialValue="USD"
                >
                  <Select>
                    <Option value="LAK">₭ LAK</Option>
                    <Option value="THB">฿ THB</Option>
                    <Option value="USD">$ USD</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={convertMutation.isLoading}>
                  คำนวณ
                </Button>
                <Button
                  onClick={() => {
                    setConvertModalVisible(false);
                    convertForm.resetFields();
                  }}
                  disabled={convertMutation.isLoading}
                >
                  ยกเลิก
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </div>
  );
};

export default ExchangeRatesPage;